// src/pages/Users/Produits/ProduitsPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "../../../components/Button/Button";

import ProductsToolbar from "./ProductsToolbar";
import ProductCard from "./ProductCard";
import SkeletonProductCard from "./SkeletonProductCard";
import EmptyState from "./EmptyState";
import VKardLink from "./VKardLink";

const DEFAULT_STATE = {
  q: "",
  sort: "order", // order | alpha
};

const FALLBACK_IMG = "/img/no-image.jpg";
const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export default function ProduitsPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("products_ui_state");
    if (!saved) return DEFAULT_STATE;
    const parsed = JSON.parse(saved);
    // 🧹 migration: on ignore l’ancien perPage s’il existe
    // eslint-disable-next-line no-unused-vars
    const { perPage, ...rest } = parsed;
    return { ...DEFAULT_STATE, ...rest };
  });
  const [debouncedQ, setDebouncedQ] = useState(ui.q);
  const [shareCopied, setShareCopied] = useState(false);

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem("products_ui_state", JSON.stringify(next));
  }, []);

  // debounce recherche
  useEffect(() => {
    const t = setTimeout(() => persistUi({ ...ui, q: debouncedQ }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [debouncedQ]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(
        "https://api.monappliclub.com/api/productsapp/allproductsapp"
      );
      if (!Array.isArray(data)) throw new Error("Réponse API invalide.");
      const sorted = [...data].sort(
        (a, b) => (a.ordre_app ?? 0) - (b.ordre_app ?? 0)
      );
      setProducts(sorted);
    } catch (e) {
      console.error("Erreur lors du chargement des produits:", e);
      setError("Impossible de charger les applications pour le moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filtered = useMemo(() => {
    const q = ui.q.trim().toLowerCase();
    let arr = products.filter((p) => {
      const name = p.nom_app?.toLowerCase() || "";
      const desc = p.description_app?.toLowerCase() || "";
      return !q || name.includes(q) || desc.includes(q);
    });

    // tri
    arr.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a.nom_app), String(b.nom_app));
      }
      return (
        (a.ordre_app ?? 0) - (b.ordre_app ?? 0) ||
        collatorFR.compare(String(a.nom_app), String(b.nom_app))
      );
    });

    return arr;
  }, [products, ui]);

  const resetFilters = () => persistUi(DEFAULT_STATE);

  const copyShareLink = async () => {
    try {
      const params = new URLSearchParams();
      if (ui.q) params.set("q", ui.q);
      if (ui.sort !== DEFAULT_STATE.sort) params.set("sort", ui.sort);
      const url = `${window.location.origin}${
        window.location.pathname
      }?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1500);
    } catch {}
  };

  return (
    <section className="w-full">
      <ScrollIndicator />
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Applications Judo"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Hero auteur */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-6">
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <h2 className="text-lg sm:text-xl font-semibold">À propos</h2>
            <p className="text-white/90 text-sm">
              Outils conçus pour les clubs, enseignants et adhérents — focus
              Judo & arts martiaux.
            </p>
          </div>
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <img
                src="https://res.cloudinary.com/dj0c0zuje/image/upload/v1744184384/PXL_20241115_211013575.MP_1_h7lbn9.jpg"
                alt="Laurent Victorin"
                className="h-28 w-28 rounded-full object-cover ring-4 ring-slate-200 dark:ring-slate-800"
              />
              <div className="text-center md:text-left">
                <h3 className="text-xl font-semibold">Laurent VICTORIN</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Auto-Entrepreneur | Développeur Web & Web Mobile | Président &
                  Enseignant de Judo
                </p>
                <p className="mt-3 text-slate-700 dark:text-slate-300">
                  Passionné de développement d'applications et de judo, je
                  conçois des applis et sites internet pour simplifier la vie
                  des clubs.
                </p>
                <div className="mt-3">
                  <a href="mailto:monappliclub@gmail.com" className="underline">
                    monappliclub@gmail.com
                  </a>{" "}
                  ·{" "}
                  <a href="tel:+33680727707" className="underline">
                    06 80 72 77 07
                  </a>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    text="Toutes mes applications sur MonAppliClub"
                    onClick={() =>
                      window.open(
                        "https://www.monappliclub.com/",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  />
                  <VKardLink />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carte globale produits */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex items-center justify-between flex-col sm:flex-row gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">
                  Catalogue d’applications
                </h2>
                <p className="text-white/90 text-sm">
                  Recherchez et découvrez les outils adaptés à votre club.
                </p>
              </div>
              <div className="text-sm">
                <span className="font-semibold">{filtered.length}</span>{" "}
                résultat(s)
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
            <ProductsToolbar
              ui={ui}
              onChange={(patch) => persistUi({ ...ui, ...patch })}
              debouncedQ={debouncedQ}
              setDebouncedQ={setDebouncedQ}
              onReset={resetFilters}
              onShare={copyShareLink}
              shareCopied={shareCopied}
            />
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonProductCard key={i} />
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="text-center py-10 text-slate-600 dark:text-slate-300">
                {error}
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <EmptyState message="Aucune application trouvée" />
            )}

            {!loading && !error && filtered.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {filtered.map((app) => (
                  <ProductCard key={app.id} app={app} fallback={FALLBACK_IMG} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Encart dons */}
        <section
          aria-label="Soutenir le développement"
          className="mt-6 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4"
        >
          {/* Header gradient */}
          <div className="px-4 py-4 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                {/* cœur qui pulse doucement */}
                <span className="animate-pulse">💜</span>
              </span>
              <div>
                <h3 className="text-lg sm:text-xl font-bold">
                  Soutenir le développement
                </h3>
                <p className="text-white/90 text-xs sm:text-sm">
                  100% facultatif — votre aide finance l’évolution des applis
                  pour vos clubs.
                </p>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-5 sm:p-6">
            {/* Promesse / transparence */}
            <ul className="grid sm:grid-cols-3 gap-3 text-sm text-slate-700 dark:text-slate-300">
              <li className="flex items-start gap-2">
                <span className="mt-0.5">🛠️</span>
                <span>
                  Maintenance & nouvelles fonctionnalités sur MonAppliClub.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">🚀</span>
                <span>
                  Création de nouvelles applications en lien avec votre
                  discipline.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">☁️</span>
                <span>
                  Si vous avez des idées d'applications, n'hésitez pas à nous en
                  faire part !
                </span>
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                Merci 🙏 — chaque participation, même symbolique, fait la
                différence.
              </p>

              {/* Lignes de dons : on affiche 1 ou plusieurs montants (pills). 
          Tu peux dupliquer avec d’autres liens Stripe si tu en crées (10 €, 15 €, etc.). */}
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href="https://buy.stripe.com/cN2aFtaqL5fC1AkcMR"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold
                     text-white bg-sky-600 hover:bg-sky-700 active:bg-sky-800
                     shadow-sm transition transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-violet-400"
                  title="Faire un don unique de 5 €"
                >
                  5 € • Don unique
                </a>

                {/* Exemple si tu ajoutes d’autres Payment Links Stripe :
        <a href="https://buy.stripe.com/TON_LINK_10" ...>10 €</a>
        <a href="https://buy.stripe.com/TON_LINK_15" ...>15 €</a>
        */}

                {/* Lien « autre montant » si tu crées un Payment Link Stripe montant libre */}
                {/* <a href="https://buy.stripe.com/TON_LINK_LIBRE" ...>Montant libre</a> */}
              </div>
            </div>

            {/* Footer rassurant */}
            <div className="mt-4 text-[11px] text-slate-500 dark:text-slate-400">
              Don volontaire, sans obligation — 100% sécurisé via Stripe.
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}
